import { projectTypesInversed } from '@teinor/erp/types/company/customer/customerProject'
import { useMemo } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { Link } from 'react-router-dom'
import { ButtonSecondary } from '../../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import TwinTable from '../../../../../../baseComponents/TwinTable'
import BaseScreenTable from '../../../../../../baseComponents/TwinTable/BaseScreenTable'
import { TableComponentBaseScreenInnerProps } from '../../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderBoolean, CustomRenderDateHours, CustomRenderString, CustomRenderStringAndColorNull, EditRow } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import ModalEditCustomerProject from '../../../../../../specificComponents/Customers/ModalEditCustomerProject'
import { CustomRenderCustomer } from '../../../../../../specificComponents/Customers/Subcomponents'
import locations from '../../../../../../utils/globals/locations'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { useParseCustomerProjectTypeByStatus } from '../../../../../../utils/hooks/customer/useProjectsAndOpportunitiesDict'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import { EmployeesPayload } from '../../../../../../utils/reducers/company/employees'
import { CustomerPayload } from '../../../../../../utils/reducers/customers/customer'
import { CustomerProjectTypePayload } from '../../../../../../utils/reducers/customers/customerProjectTypes'
import { getAllClientManagerEmployees, getConfigParam } from '../../../../../../utils/reducers/getters'
import { CustomerProjectWithDateLastAction } from '../CustomerProjects/types'
import ModalCreateCustomerOpportunity from './ModalCreateCustomerOpportunity'


interface CustomerOpportunitiesProps extends ComponentWithPermissions { }

const CustomerOpportunities: React.FC<CustomerOpportunitiesProps> = ({ ...props }) => {
    return (
        <CustomerOpportunitiesInner {...props} RightHeader={
            <Link to={locations.customerOppStatusFlow} className='mr-10'>
                <ButtonSecondary>
                    <TwinTrans transKey='changeView'>Cambiar vista</TwinTrans>
                </ButtonSecondary>
            </Link>
        } />
    )
}

interface CustomerOpportunitiesInnerProps extends CustomerOpportunitiesProps {
    RightHeader?: JSX.Element | undefined
}

export const CustomerOpportunitiesInner: React.FC<CustomerOpportunitiesInnerProps> = ({ userPermissions, RightHeader }) => {
    const { t } = useTwinTranslation()
    const pageData = {
        title: t('opportunities', 'Oportunidades'),
        newText: t('addOpportunities', 'Añadir oportunidad'),
    }

    return (
        <BaseScreenTable TableComponent={TableCustomerOpportunitiesConnect} pageData={pageData} CreateModalComponent={ModalCreateCustomerOpportunity} EditModalComponent={ModalEditCustomerProject} userPermissions={userPermissions} RightHeader={RightHeader} />
    )
}

type TableCustomerOpportunitiesProps = TableComponentBaseScreenInnerProps & ReduxCustomerAndProjectTypes & {
}

const TableCustomerOpportunities: React.FC<TableCustomerOpportunitiesProps> = ({ setTableInstance, setOpened, customers, customerProjectTypes, employees }) => {
    const { t } = useTwinTranslation()
    const { myParsedCustomerProjectTypes } = useParseCustomerProjectTypeByStatus({ customerProjectTypes, projectTypeStatus: 'opportunity' })
    const myEmployees = useMemo(() => getConfigParam('showOnlyClientManagerEmployees') === 'on' ? getAllClientManagerEmployees() || {} : employees || {}, [employees])
    const columns: ColumnTableSchema<CustomerProjectWithDateLastAction> = [
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            width: 80,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('id', 'ID') }
            },
            searchKey: 'CustomerProject.id'
        },
        {
            id: 'customer',
            dataKey: 'CustomerId',
            label: t('customer', 'Cliente'),
            minWidth: 130,
            customRender: (_parameterValue, allRowData) => <CustomRenderCustomer allRowData={allRowData} />,
            sortable: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: {
                    items: customers?.all || {}, label: t('customer', 'Cliente')
                },
            },
            searchKey: 'Customer.name'
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            searchKey: 'CustomerProject.name'

        },
        {
            id: 'description',
            dataKey: 'description',
            label: t('description', 'Descripción'),
        },
        {
            id: 'CustomerProjectType',
            dataKey: 'CustomerProjectTypeId',
            label: t('opportunityType', 'Tipo de oportunidad'),
            customRender: (parameterValue, allRowData) => <CustomRenderString value={allRowData?.CustomerProjectType?.name} />,
            minWidth: 150,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: myParsedCustomerProjectTypes || {}, label: t('opportunityType', 'Tipo de oportunidad') }
            },
            searchKey: 'CustomerProject.CustomerProjectTypeId'
        },
        {
            id: 'CurrentCProjectStatusId',
            dataKey: 'CurrentCProjectStatusId',
            label: t('status', 'Estado'),
            customRender: (_parameterValue, allRowData) => <CustomRenderStringAndColorNull value={allRowData?.CurrentCProjectStatus?.name} color={allRowData?.CurrentCProjectStatus?.color} />,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('status', 'Estado') }
            },
            searchKey: 'CurrentCProjectStatus.name',
            sortable: true,
            sortKey: 'CurrentCProjectStatus.order'
        },
        {
            id: 'ResponsibleEmployeeId',
            dataKey: 'Customer',
            label: t('ourManager', 'Nuestro gestor'),
            minWidth: 170,
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.Customer?.ResponsibleEmployee?.fullname_short} />,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSearchBar',
                extraComponentData: { items: myEmployees, label: t('ourManager', 'Nuestro gestor'), fieldName: 'fullname_short' }
            },
            sortable: true,
            hideColumnByDefault: true,
            searchSingleKey: 'Customer.ResponsibleEmployee.id',
            searchKey: 'Customer.ResponsibleEmployee.fullname_short'
        },
        {
            id: 'dateLastAction',
            dataKey: 'dateLastAction',
            label: t('dateLastAction', 'Fecha última acción'),
            customRender: (parameterValue) => <CustomRenderDateHours value={parameterValue} />,
            sortable: true,
            hideColumnByDefault: true
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: {
                    items: {
                        'true': { name: t('actived', 'Activados') },
                        'false': { name: t('disabled', 'Desactivados') },
                        'all': { name: t('all', 'Todos') }
                    }, label: t('active', 'Activo')
                }
            },
            searchKey: 'CustomerProject.active',
            generalSearchable: false
        },
        {
            id: 'edit',
            dataKey: 'id',
            label: '',
            width: 50,
            simpleLabel: t('edit', 'Editar'),
            customRender: (parameterValue, allRowData) => <EditRow key={'edit' + parameterValue} {...{ parameterValue, allRowData }} />
        },
    ]
    return (
        <TwinTable name='CustomerOpportunities' columns={columns} defaultOrder={{ order: 'desc', defaultOrderField: 'createdAt' }} extraWhereParams={{ projectType: projectTypesInversed['opportunity'] }} getDataFrom='/api/app/customer/customerProject/getAllCustomerProjects'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })}
            setTableInstance={setTableInstance} />
    )
}

const customersAndProjectTypesDispatch = {
    setCustomers: (payload: CustomerPayload) => ({ type: 'CHANGE_CUSTOMER', payload }),
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setEmployees: (payload: EmployeesPayload) => ({ type: 'CHANGE_EMPLOYEE', payload }),
}

export type ReduxCustomerAndProjectTypes = ConnectedProps<typeof customersAndProjectTypesConnect>
const mapCustomersAndProjectTypesConnector = (state: AllReduxPayloads) => ({ customers: state.customers, customerProjectTypes: state.customerProjectTypes, employees: state.employees })
const customersAndProjectTypesConnect = connect(mapCustomersAndProjectTypesConnector, customersAndProjectTypesDispatch)

const customersAndProjectTypesConnectLoading = withLoading(TableCustomerOpportunities, [{ fetchUrl: '/api/app/customer/getAllCustomersComplete', propName: 'customers', setFunctionName: 'setCustomers' }, { fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, { fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])

const TableCustomerOpportunitiesConnect = customersAndProjectTypesConnect(customersAndProjectTypesConnectLoading)


export default CustomerOpportunities